/* eslint-disable @typescript-eslint/no-explicit-any */
import { skipToken, useSuspenseQuery } from '@apollo/client';
import { useMemo, useEffect, useDeferredValue, useRef } from 'react';

import { SearchMode } from '@xing-com/crate-common-graphql-types';
import { useNavigate, useLocation } from '@xing-com/crate-core-router';
import { XING_JOBS_SEARCH_KI_PATH } from '@xing-com/crate-core-routes';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import {
  AI_SEARCH_INIT_PROMPT_PARAM,
  SEARCH_PAGE_SIZE,
} from '@xing-com/crate-jobs-constants';
import { getJobSerpsSearchQueryVariables } from '@xing-com/crate-jobs-domain-serps-helpers';
import { useSerpsMatch } from '@xing-com/crate-jobs-domain-serps-hooks';
import { JobSearchByQueryDocument } from '@xing-com/crate-jobs-graphql';
import { useIsAiSearch, useSearchState } from '@xing-com/crate-jobs-hooks';
import { useExperiments } from '@xing-com/hub';

import { getFiltersFromAggregations, getSearchCount } from '../helpers';

import { useSearchConsumer } from './use-search-consumer';

type UseSearchArgs = {
  isEmptyState?: boolean;
};

function sortObject(object: any): any {
  if (!object || typeof object !== 'object') {
    return object;
  }

  if (Array.isArray(object)) {
    return object.map((item) => sortObject(item));
  }

  const sortedObj: Record<string, any> = {};
  const keys = Object.keys(object).sort((key1, key2) => {
    key1 = key1.toLowerCase();
    key2 = key2.toLowerCase();
    if (key1 < key2) return -1;
    if (key1 > key2) return 1;
    return 0;
  });

  for (const key of keys) {
    sortedObj[key] = sortObject(object[key]);
  }

  return sortedObj;
}

const useSkipInitialAiSearch = (): boolean => {
  const { getSearchParameter } = useSearchParameter();

  const initPromptOnLoad = !!getSearchParameter(AI_SEARCH_INIT_PROMPT_PARAM);

  return initPromptOnLoad;
};

export const useSearch = ({
  isEmptyState,
  // Return types are inferred
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}: UseSearchArgs = {}) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { isLoggedOut } = useLoginState();
  const consumer = useSearchConsumer();
  const { searchQuery, sort, page, sincePeriod, since } = useSearchState();
  const isAiSearch = useIsAiSearch();

  const serps = useSerpsMatch();
  const isSerps = serps && isLoggedOut;
  const serpsSearchQuery = isSerps
    ? getJobSerpsSearchQueryVariables(serps)
    : {};

  const { loading: areExperimentsLoading } = useExperiments();

  const finalKeywords = searchQuery.keywords || serpsSearchQuery.keywords;
  const finalLocation = searchQuery.location || serpsSearchQuery.location;
  const finalFilter = searchQuery.filter || serpsSearchQuery.filter;
  // BE needs a date format with a Zero Timezone, but the SA service doesn't include it
  const finalSince = since && since.slice(-1) !== 'Z' ? `${since}Z` : since;

  // We need this for a special case, which is when the CS search is entered
  // with one of the suggestions automatically running
  const skipAiSearch = useSkipInitialAiSearch();

  // Memoize variables with deep comparison via serialized key.
  // useDeferredValue uses identity (===) comparison, so passing a new object
  // on every render causes infinite deferred re-renders that never converge.
  const variablesKey = JSON.stringify([
    searchQuery,
    finalKeywords,
    finalLocation,
    finalFilter,
    isSerps,
    isEmptyState,
    consumer,
    sort,
    page,
    sincePeriod,
    finalSince,
    isAiSearch,
  ]);

  const variables = useMemo(() => {
    const mergedQuery = sortObject({
      ...searchQuery,
      ...(finalKeywords && { keywords: finalKeywords }),
      ...(finalLocation && { location: finalLocation }),
      ...(finalFilter && { filter: finalFilter }),
    });

    if (!isSerps && isEmptyState) {
      mergedQuery.location = undefined;
    }

    return {
      query: mergedQuery,
      consumer,
      sort,
      limit: SEARCH_PAGE_SIZE,
      offset: SEARCH_PAGE_SIZE * (page - 1),
      sincePeriod,
      since: finalSince,
      trackRecent: !isEmptyState,
      searchMode: isAiSearch ? SearchMode.Semantic : SearchMode.Normal,
      boundedTotals: isAiSearch,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variablesKey]);

  const initialSearchQueryRef = useRef<string>();

  const deferredVariables = useDeferredValue(variables);

  const isLoading = deferredVariables !== variables || skipAiSearch;

  const { data, fetchMore, refetch } = useSuspenseQuery(
    JobSearchByQueryDocument,
    skipAiSearch || areExperimentsLoading
      ? skipToken
      : {
          variables: deferredVariables,
          errorPolicy: 'all',
        }
  );

  const results = data?.jobSearchByQuery;

  // if it's a SERP page, redirect to "search" needed on any change of sorting/filtering params
  useEffect(() => {
    if (!isSerps) {
      return;
    }

    const searchQueryStringified = JSON.stringify(searchQuery);
    const hasSearchQuery = Object.keys(searchQuery).length > 0;

    // Store initial search query on first render
    if (initialSearchQueryRef.current === undefined && hasSearchQuery) {
      initialSearchQueryRef.current = searchQueryStringified;
      return;
    }

    // Navigate when search query changes from initial
    if (
      initialSearchQueryRef.current !== undefined &&
      initialSearchQueryRef.current !== searchQueryStringified
    ) {
      navigate(
        `${XING_JOBS_SEARCH_KI_PATH}?${new URLSearchParams(search).toString()}`,
        { replace: true }
      );
    }
  }, [isSerps, search, navigate, searchQuery]);

  const aggregations = useMemo(() => {
    const aggregations = results?.aggregations ?? {};
    const filters = getFiltersFromAggregations(aggregations, isAiSearch);
    return { aggregations, filters };
  }, [results, isAiSearch]);

  // We only want to use the previous results when the query is loading
  const filterCollection = results?.searchQuery.body?.filterCollection ?? [];

  const extractedFilters = useMemo(() => {
    const enabledFilters =
      results?.appliedFilters?.extractedFilters?.enabled ?? [];

    const disabledFilters =
      results?.appliedFilters?.extractedFilters?.disabled ?? [];

    return enabledFilters.filter((filter) => {
      if (!filter) return false;

      const isDisabled = disabledFilters.some((disabledFilter) => {
        if (!disabledFilter) return false;

        if (
          disabledFilter.__typename === 'JobFilterDisabledSalary' &&
          filter.__typename === 'JobFilterSalary'
        ) {
          return !!disabledFilter.salary;
        }

        if (
          disabledFilter.__typename === 'JobFilterLocation' &&
          filter.__typename === 'JobFilterLocation'
        ) {
          return (
            disabledFilter.location?.localizationValue ===
            filter.location?.localizationValue
          );
        }

        return (
          'entityId' in filter &&
          'entityId' in disabledFilter &&
          disabledFilter.entityId === filter.entityId &&
          disabledFilter.__typename === filter.__typename
        );
      });

      return !isDisabled;
    });
  }, [results]);

  return {
    aggregations,
    isLoading,
    results,
    filterCollection,
    extractedFilters,
    searchCount: results ? getSearchCount(results.searchQuery.guid) : undefined,
    fetchMore,
    refetch,
  };
};
